import { useState } from 'react';

// material-ui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';

// project imports
import { useDispatch } from 'store';
import { createOrganization } from 'store/slices/organizations';
import { updateProfile as updateAuthProfile } from 'store/slices/auth';
import { useNavTheme } from 'contexts/NavThemeContext';

// assets
import { IconBuilding, IconWorld, IconRocket } from '@tabler/icons-react';

interface OrganizationSetupModalProps {
  open: boolean;
  userId: string;
  onComplete: () => void;
}

export default function OrganizationSetupModal({ open, userId, onComplete }: OrganizationSetupModalProps) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';

  // Monochrome button colors (matching integrations/settings)
  const primaryButtonBg = isLight ? '#1e293b' : '#fafafa';
  const primaryButtonText = isLight ? '#fafafa' : '#0a0a0f';

  const handleCreateOrganization = async (values: { name: string; website?: string }) => {
    try {
      setLoading(true);

      // Create organization and associate with user
      await dispatch(createOrganization({
        name: values.name,
        website: values.website,
        userId,
      })).unwrap();

      // Refresh user data to get updated organizationId
      await dispatch(updateAuthProfile()).unwrap();

      enqueueSnackbar('Organization created successfully!', {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });

      // Trigger completion callback (will close modal)
      onComplete();
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      enqueueSnackbar('Something went wrong during organization creation. Please try again.', {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  // Clean TextField styles matching the app design system
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      background: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.03)',
      borderRadius: '10px',
      '& fieldset': {
        borderColor: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
      },
      '&:hover fieldset': {
        borderColor: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)',
      },
      '&.Mui-focused fieldset': {
        borderColor: isLight ? '#1e293b' : '#fafafa',
        borderWidth: '1px',
      },
      '&.Mui-error fieldset': {
        borderColor: '#ef4444',
      },
    },
    '& .MuiInputLabel-root': {
      color: tokens.textMuted,
      '&.Mui-focused': {
        color: tokens.textSecondary,
      },
      '&.Mui-error': {
        color: '#ef4444',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: tokens.textPrimary,
      py: 1.5,
      '&::placeholder': {
        color: tokens.textMuted,
        opacity: 1,
      },
    },
    '& .MuiFormHelperText-root': {
      color: tokens.textMuted,
      '&.Mui-error': {
        color: '#ef4444',
      },
    },
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {
            background: isLight ? '#ffffff' : 'rgba(15, 16, 24, 0.98)',
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            borderRadius: '16px',
            boxShadow: isLight
              ? '0 25px 80px rgba(15, 23, 42, 0.15)'
              : '0 25px 80px rgba(0, 0, 0, 0.5)',
          }
        },
        backdrop: {
          sx: {
            backgroundColor: isLight ? 'rgba(15, 23, 42, 0.4)' : 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 0, pt: 3, px: 3 }}>
        <Stack alignItems="center" spacing={2}>
          {/* Icon Badge */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
            }}
          >
            <IconRocket size={28} style={{ color: tokens.textMuted }} />
          </Box>

          {/* Title */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                fontFamily: '"Satoshi", "Inter", sans-serif',
                color: tokens.textPrimary,
                letterSpacing: '-0.01em',
                mb: 0.5,
              }}
            >
              Set Up Your Organization
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif',
                color: tokens.textMuted,
                lineHeight: 1.6,
              }}
            >
              Create your organization to start managing your team, projects, and AI-powered workflows.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Formik
        initialValues={{
          name: '',
          website: '',
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .required('Organization name is required')
            .min(2, 'Name must be at least 2 characters')
            .max(255, 'Name must not exceed 255 characters'),
          website: Yup.string().url('Must be a valid URL').max(255, 'Website must not exceed 255 characters'),
        })}
        onSubmit={handleCreateOrganization}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  required
                  disabled={loading}
                  placeholder="Acme Corporation"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Box sx={{ mr: 1.5, display: 'flex', color: tokens.textMuted }}>
                          <IconBuilding size={20} />
                        </Box>
                      )
                    }
                  }}
                  sx={textFieldSx}
                />

                <TextField
                  fullWidth
                  label="Website (Optional)"
                  name="website"
                  value={values.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.website && errors.website)}
                  helperText={touched.website && errors.website}
                  placeholder="https://yourcompany.com"
                  disabled={loading}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Box sx={{ mr: 1.5, display: 'flex', color: tokens.textMuted }}>
                          <IconWorld size={20} />
                        </Box>
                      )
                    }
                  }}
                  sx={textFieldSx}
                />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
              <Box
                component="button"
                type="submit"
                disabled={loading || isSubmitting}
                sx={{
                  width: '100%',
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  borderRadius: '10px',
                  background: primaryButtonBg,
                  border: 'none',
                  color: primaryButtonText,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  fontFamily: '"Satoshi", sans-serif',
                  cursor: loading || isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: loading || isSubmitting ? 0.6 : 1,
                  transition: 'all 0.15s ease',
                  '&:hover:not(:disabled)': {
                    background: isLight ? '#0f172a' : '#ffffff',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={18} sx={{ color: primaryButtonText }} />
                    <span>Creating Organization...</span>
                  </>
                ) : (
                  <>
                    <IconRocket size={18} />
                    <span>Create Organization</span>
                  </>
                )}
              </Box>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
